import { last } from 'lodash'
import loader from '../partials/loader'

const quickOrder = (data) => ({
    ...window.helpers,
    ...loader,
    loading: true,
    loadingProjects: true,
    loadingAlternatives: true,
    saving: false,
    savingError: false,
    lines: [],
    id: null,
    secureKey: null,
    shareKey: null,
    name: '',
    unsavedName: '',
    hideUnavailableWarning: false,
    saved: false,
    show: false,
    currentIso: window.accuvars.language.iso_code,
    currency: window.Alpine.store('currency'),
    deleteLineIndex: null,
    alternativeIndex: null,
    projects: [],
    alternatives: [],
    hits: 0,
    buttonHeld: false,
    pagination: {
        start: 1,
        stop: 1,
        page: 1,
        step: 10,
        pages: 1,
        previousPages: [],
    },
    baseAuthUrl: '/authentication',
    init() {
        if (data.tax_rate) {
            this.taxRate = data.tax_rate
        }
        if (data.url) {
            this.url = data.url
        }
        if (data.baseAuthUrl) {
            this.baseAuthUrl = data.baseAuthUrl
        }
        const params = new URLSearchParams(document.location.search)
        const id = params.get('id')
        const key = params.get('key')
        
        // Only load if we don't already have this project loaded
        if (!this.id || this.id !== id || (key && this.shareKey !== key)) {
            this.load(id, key)
        }
    },
    loadProjects() {
        window.api.get(`/quick-order?action=list&page=${this.pagination.page}&limit=${this.pagination.step}`)
            .then((response) => {
                this.loadingProjects = false
                this.projects = response.data.quick_orders.data
                this.hits = response.data.quick_orders.count
            }).catch((error) => {
                this.loadingProjects = false
            })
    },
    load(id, key, create = false) {
        // Check if we're already viewing this shared project
        if (id && key && this.id === id && this.shareKey === key) {
            return // Already viewing this shared project, no need to reload
        }
        
        this.loading = true
        window.api.get(`/quick-order?action=load${id ? `&id=${id}` : ''}${key ? `&key=${key}` : ''}${create ? '&create=1' : ''}`)
            .then((response) => {
                this.loading = false
                this.name = response.data.quick_order.name
                this.id = response.data.quick_order.id
                this.secureKey = response.data.quick_order.secure_key
                this.shareKey = response.data.quick_order.share_key
                this.saved = response.data.quick_order.saved
                this.lines = response.data.lines.map((line) => ({
                    qty: 1, 
                    stock: 0,
                    ...line,
                }))

                this.setPricesAndStock(this.lines.map((line, index) => ({
                    id: line.id,
                    quantity: line.qty,
                    index,
                })))

                this.hideUnavailableWarning = false

                if (this.name) {
                    this.show = true
                }
                if (id && id != response.data.quick_order.id) {
                    window.Alpine.store('general').showOverlay = true
                    window.Alpine.store('general').showQuickOrderSharedModal = true
                    window.history.replaceState(null, '', window.location.pathname)
                    this.unsavedName = response.data.quick_order.name
                    window.dispatchEvent(new CustomEvent('gtmdata', {
                        detail: {
                            event: 'gtm_quick_order_open_shared',
                        },
                    }))
                } else if (create || id) {
                    // Update URL and history for new or loaded quick orders
                    const newUrl = new URL(window.location.href)
                    newUrl.searchParams.set('id', response.data.quick_order.id)
                    if (response.data.quick_order.secure_key) {
                        newUrl.searchParams.set('key', response.data.quick_order.secure_key)
                    }
                    window.history.replaceState(null, '', newUrl.toString())

                }

                //dont overwrite if loading from shared project
                if (this.unsavedName && !id) {
                    this.save({ name: this.unsavedName })
                    this.unsavedName = ''
                }

                this.createRows(Math.max(1, 3 - this.lines.length))

                this.loadProjects()

                if (id === this.id) {
                    window.dispatchEvent(new CustomEvent('gtmdata', {
                        detail: {
                            event: 'gtm_quick_order_open_saved',
                        },
                    }))
                }
            }).catch((error) => {
                this.loading = false
                this.displayError(error.response?.data?.message)
            })
    },
    save(options, requireLogin = false) {
        this.saving = true
        this.savingError = false
        window.api.post('/quick-order?action=save', {
            id: this.id,
            secure_key: this.secureKey,
            name: this.name,
            lines: this.lines.filter((line) => line.id),
            ...options,
        }).then((response) => {
            if (!this.saved && response.data.quick_order.saved) {
                window.dispatchEvent(new CustomEvent('gtmdata', {
                    detail: {
                        event: 'gtm_quick_order_save',
                    },
                }))
            }
            this.saving = false
            this.saved = response.data.quick_order.saved
            this.name = response.data.quick_order.name
            this.hideUnavailableWarning = false

            // Update URL when project is saved
            if (this.saved) {
                const newUrl = new URL(window.location.href)
                newUrl.searchParams.set('id', response.data.quick_order.id)
                if (response.data.quick_order.secure_key) {
                    newUrl.searchParams.set('key', response.data.quick_order.secure_key)
                }
                window.history.replaceState(null, '', newUrl.toString())
            }

            if (requireLogin && !this.saved) {
                window.location.href = this.authUrl
            }
            if (this.name) {
                this.show = true
            }
        }).catch((error) => {
            this.saving = false
            this.savingError = true
            this.displayError(error.response?.data?.message)
        })
    },
    saveLine(productId, quantity) {
        this.saving = true
        this.savingError = false
        window.api.post('/quick-order?action=save_line', {
            id: this.id,
            secure_key: this.secureKey,
            product_id: productId,
            quantity,
        }).then((response) => {
            this.saving = false
        }).catch((error) => {
            this.saving = false
            this.savingError = true
            this.displayError(error.response?.data?.message)
        })
    },
    refreshCurrency() {
        this.currency = window.Alpine.store('currency')
        const ids = []
        this.lines.forEach((item, index) => {
            if (item.id !== null && item.qty !== null) {
                ids.push({
                    id: item.id,
                    quantity: item.qty,
                    index,
                })
            }
        })
        this.setPricesAndStock(ids)
    },
    setPricesAndStock(products) {
        window.api.getPricesAndStock(products).then((productResults) => {
            const productData = productResults

            Object.keys(productData).forEach((key) => {
                const line = this.lines[productData[key].index]
                const product = productData[key].data

                line.product = product
                line.stock = product.secondary_stock
                line.lead_time = product.secondary_lead_time

                // Store the base price and price breaks for future calculations
                line.base_price = parseFloat(product.price).toFixed(2)
                line.price_breaks = product.price_breaks
                line.minimal_quantity = product.minimal_quantity
                line.available = product.available_for_order && !product.date_discontinued

                this.updateLinePrice(line)

                if (!line.available) {
                    line.price = 0
                    line.line_total = 0
                }
            })
        }).catch((error) => {
            this.displayError(error.response?.data?.message)
        })
    },
    updateLinePrice(line) {
        if (!line.available) {
            line.price = 0
            line.line_total = 0
            return
        }

        line.price = line.base_price
        line.saving_percentage = 0

        // find related price break
        if (line.price_breaks && line.price_breaks.length > 0) {
            for (let x = 0; x < line.price_breaks.length; x += 1) {
                const priceBreak = line.price_breaks[x]
                if (parseInt(priceBreak.from_quantity, 10) <= parseInt(line.qty, 10)) {
                    line.price = priceBreak.price
                    line.saving_percentage = priceBreak.saving_percentage
                    break
                }
            }
        }

        line.line_total = parseFloat(line.price * line.qty).toFixed(2)

        const stockLevels = [
            { quantity: line.product.quantity, lead_time: 0 },
            { quantity: line.product.ex_stock, lead_time: line.product.ex_lead_time },
            { quantity: line.product.secondary_stock, lead_time: line.product.secondary_lead_time },
        ]

        // Filter out stock levels with 0 quantity and find appropriate level
        const availableStockLevels = stockLevels.filter(level => level.quantity > 0)
        
        const selectedStockLevel = availableStockLevels.find(level => level.quantity >= line.qty) ?? last(availableStockLevels)
        
        line.stock = selectedStockLevel?.quantity ?? 0
        line.lead_time = selectedStockLevel?.lead_time ?? 0
    },
    processSelection(data, index) {
        if (this.lines.filter((line) => line.id === data.objectID).length > 0) {
            this.displayError('This component has already been added to your list.')
            return
        }

        if (this.lines.filter((line) => !line.id).length <= 1) {
            this.createRow()
        }

        if (index === undefined) {
            index = this.lines.findIndex((line) => !line.id)
        }

        if (this.lines[index].id) {
            this.saveLine(this.lines[index].id, 0)
        }

        this.lines[index] = {
            id: data.objectID,
            title: data[this.currentIso].title,
            reference: data.reference,
            img: data.image,
            link: data[this.currentIso].uri,
            qty: this.lines[index]?.qty || data.qty || 1,
            price: 0.00,
            line_total: 0.00,
            stock: 0,
            saving_percentage: 0,
            minimal_quantity: 1,
            available: true,
        }

        this.saveLine(data.objectID, this.lines[index].qty)

        this.setPricesAndStock([{
            id: data.objectID,
            quantity: 1,
            index,
        }])

        window.dispatchEvent(new CustomEvent('gtmdata', {
            detail: {
                event: 'gtm_quick_order_add_product',
            },
        }))
    },
    changingQuantity(index, amount=0) {
        const line = this.lines[index]
        let iterations = 0
        clearTimeout(this.heldTimeout)
        this.heldTimeout = null

        const updateQuantity = () => {
            const newQty = Math.min(Math.max(1, parseInt(line.qty, 10) + amount), 1000000)
            line.qty = newQty
            
            if (line.qty > 0) {
                this.saveLine(line.id, line.qty)
            }

            this.updateLinePrice(line)
        }

        const checkButtonHeld = () => {
            updateQuantity()
            const delay = 1500 / (iterations + 3)
            iterations += 1
            this.heldTimeout = setTimeout(() => {
                if (this.buttonHeld) {
                    checkButtonHeld()
                }
            }, delay)
        }

        // Only update on initial click if not already holding
        if (!this.buttonHeld) {
            updateQuantity()
        }
        
        // Start hold timer if button is held
        if (this.buttonHeld) {
            checkButtonHeld()
        }
    },
    quantityDown(index) {
        const line = this.lines[index]
        line.qty = parseInt(line.qty, 10) - 1
        this.changingQuantity(index, -1)
    },
    quantityUp(index) {
        const line = this.lines[index]
        line.qty = parseInt(line.qty, 10) + 1
        this.changingQuantity(index, 1)
    },
    submitBasket() {
        window.dispatchEvent(new CustomEvent('gtmdata', {
            detail: {
                event: 'gtm_quick_order_add_to_cart',
            },
        }))

        window.dispatchEvent(new CustomEvent('add-to-cart', {
            detail: {
                products: this.lines.filter((line) => line.id && line.available)
                    .map((line, index) => ({
                        productId: line.id,
                        quantity: Math.max(line.qty, line.minimal_quantity),
                        additive: true,
                        position: index,
                        productDetails: {
                            name: line.title,
                            reference: line.reference,
                            priceUnitExTax: line.price,
                            categoryId: 0,
                        },
                    })),
                fromPage: 'quick-order',
                cartAction: 'add',
            },
        }))
    },
    createRow() {
        this.lines.push({
            id: null,
            title: null,
            reference: null,
            img: 'https://i.accu.co.uk/default.png',
            link: '',
            qty: 0,
            price: 0.00,
            line_total: 0.00,
            stock: 0,
            saving_percentage: 0,
            minimal_quantity: 0,
        })
    },
    createRows(count) {
        [...Array(count)].forEach(() => this.createRow())
    },
    removeRow(index) {
        if (this.lines[index].id) {
            this.saveLine(this.lines[index].id, 0)
            window.dispatchEvent(new CustomEvent('gtmdata', {
                detail: {
                    event: 'gtm_quick_order_remove_product',
                },
            }))
        } else {
            window.dispatchEvent(new CustomEvent('gtmdata', {
                detail: {
                    event: 'gtm_quick_order_remove_line',
                },
            }))
        }
        this.lines.splice(index, 1)
    },
    removeAllRows() {
        const length = this.lines.length
        for (let i = 0; i < length; i += 1) {
            this.removeRow(0)
        }
        this.createRow()
    },
    checkMinAndReset(index) {
        const line = this.lines[index]
        if (!line.qty || line.qty < 1) {
            line.qty = 1
            this.changingQuantity(index)
        }
    },
    formatDate(date) {
        const d = new Date(date)
        return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`
    },
    async loadAlternatives(lineIndex) {
        window.dispatchEvent(new CustomEvent('gtmdata', {
            detail: {
                event: 'gtm_quick_order_find_alternatives_click',
            },
        }))

        this.loadingAlternatives = true
        this.alternativeIndex = lineIndex
        window.Alpine.store('general').showQuickOrderAlternativesModal = true
        window.Alpine.store('general').showOverlay = true

        const line = this.lines[lineIndex]
        const response = await window.api.get(`/quick-order?action=alternatives&product_id=${line.id}`)
        const query = response.data.query

        const queries = [
            {
                indexName: 'new_products',
                query: query,
                restrictSearchableAttributes: [
                    'manufacturer_name',
                    'reference',
                    `${this.currentIso}.title`,
                    `${this.currentIso}.meta_keywords`,
                    `${this.currentIso}.meta_description`,
                    `${this.currentIso}.category_name`,
                    `${this.currentIso}.attributes`,
                ],
                params: {
                    hitsPerPage: 10,
                },
            },
        ]

        window.api.search(queries).then((searchResults) => {
            if (searchResults.length === 0) {
                this.alternatives = []
            } else {
                const indexResult = searchResults[0]
                this.alternatives = indexResult.hits.map((product) => ({
                    qty: 1,
                    ...product,
                }))
                this.loadingAlternatives = false
            }
            // reset selection after search
            this.navigationSelectionIndex = 0
        }).catch(() => {
            this.alternatives = []
            this.loadingAlternatives = false
        })
    },

    setAlternative(product) {
        this.processSelection(product, this.alternativeIndex)
        this.alternativeIndex = undefined

        window.dispatchEvent(new CustomEvent('gtmdata', {
            detail: {
                event: 'gtm_quick_order_alternative_added',
            },
        }))
    },
    openShareModal() {
        window.Alpine.store('general').showQuickOrderShareModal = true
        window.Alpine.store('general').showOverlay = true

        window.dispatchEvent(new CustomEvent('gtmdata', {
            detail: {
                event: 'gtm_quick_order_share_click',
            },
        }))
    },
    displayError(message, title) {
        window.dispatchEvent(new CustomEvent('create-toast', {
            detail: {
                title: title || 'We encountered a problem...',
                message: message || 'An unknown error occurred.',
                type: 'error',
            },
        }))
    },
    get componentCount() {
        let componentCount = 0

        this.lines.forEach((row) => {
            if (row.id != null && row.qty != null) {
                componentCount += Math.max(0, (parseInt(row.qty, 10) || 0))
            }
        })

        return componentCount
    },
    get totalValue() {
        let total = 0

        this.lines.forEach((row) => {
            if (row.id != null && row.qty != null) {
                total += Math.max(0, parseFloat(row.line_total))
            }
        })

        return parseFloat(total).toFixed(2)
    },
    get shareUrl() {
        return `${window.location.protocol}//${window.location.host}${window.location.pathname}?id=${this.id}&key=${this.shareKey}`
    },
    get guestUrl() {
        return `${window.location.protocol}//${window.location.host}${window.location.pathname}?id=${this.id}&key=${this.secureKey}`
    },
    get authUrl() {
        return `${this.baseAuthUrl}?back=${encodeURIComponent(this.guestUrl)}`
    },
})
document.addEventListener('alpine:init', () => {
    window.Alpine.data('quickOrder', quickOrder)
})
